import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Slide,
  CircularProgress,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../context/authContext';
import { PushNotificationService } from '../../services/pushNotificationService';
import { toast } from '../../utils/toast';

interface PushNotificationPromptProps {
  /** When to show the prompt */
  show: boolean;
  /** Callback when prompt is dismissed (either enabled or closed) */
  onDismiss: () => void;
  /** Optional custom message */
  message?: string;
  /** Position of the prompt */
  position?: 'top' | 'bottom';
}

const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({
  show,
  onDismiss,
  message,
  position = 'bottom',
}) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  // Don't render if notifications aren't supported
  if (!PushNotificationService.isSupported()) {
    return null;
  }

  // Don't render if already have permission
  if (PushNotificationService.hasNotificationPermission()) {
    return null;
  }

  const handleEnable = async () => {
    setLoading(true);
    try {
      const success = await authContext?.enablePushNotifications?.();
      if (success) {
        toast.success(t('Push notifications enabled! You\'ll be notified when AI needs your input.'));
        onDismiss();
      } else {
        toast.error(t('Failed to enable notifications. Please check your browser settings.'));
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast.error(t('Failed to enable notifications'));
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Store dismissal in localStorage so we don't show again for a while
    localStorage.setItem('pushNotificationPromptDismissed', Date.now().toString());
    onDismiss();
  };

  return (
    <Slide direction={position === 'bottom' ? 'up' : 'down'} in={show} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          [position]: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 480,
          width: 'calc(100% - 48px)',
          p: 2.5,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          zIndex: 1300,
          border: '1px solid',
          borderColor: 'primary.light',
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, #121212 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.background.dialog} 100%)`,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <NotificationsActiveIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('Stay Updated with AI Automation')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {message || t('Enable notifications to know when AI finds great matches or needs your input to continue negotiations.')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleEnable}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ minWidth: 100 }}
            >
              {loading ? t('Enabling...') : t('Enable')}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'text.secondary' }}
            >
              {t('Not now')}
            </Button>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.disabled',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Slide>
  );
};

export default PushNotificationPrompt;

/**
 * Hook to manage push notification prompt visibility
 */
export const usePushNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);

  const shouldShowPrompt = (): boolean => {
    // Don't show if notifications aren't supported
    if (!PushNotificationService.isSupported()) return false;
    
    // Don't show if already have permission
    if (PushNotificationService.hasNotificationPermission()) return false;

    // Don't show if dismissed recently (within 7 days)
    const dismissed = localStorage.getItem('pushNotificationPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDays) return false;
    }

    return true;
  };

  const triggerPrompt = () => {
    if (shouldShowPrompt()) {
      setShowPrompt(true);
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
  };

  return {
    showPrompt,
    triggerPrompt,
    dismissPrompt,
    shouldShowPrompt,
  };
};
